import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getWalletBalance, getWalletTransactions, requestCashout,
  getDriverTripInvoice, downloadFromSignedUrl,
} from '../../services/walletService';
import toast from 'react-hot-toast';
import BackButtonBottom from '../../components/common/BackButtonBottom';

export default function DriverWallet() {
  const { currentUser } = useAuth();
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cashingOut, setCashingOut] = useState(false);

  const load = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const [bal, txs] = await Promise.all([
        getWalletBalance(currentUser.uid),
        getWalletTransactions(currentUser.uid),
      ]);
      setBalance(bal);
      setTransactions(txs);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);

  const handleCashout = async () => {
    setCashingOut(true);
    try {
      const result = await requestCashout(currentUser.uid);
      toast.success(result?.message || 'Cashout requested');
      await load();
    } catch (err) {
      toast.error(err.message || 'Cashout failed');
    }
    setCashingOut(false);
  };

  if (loading) return <div className="flex justify-center p-20"><div className="spinner spinner-lg" /></div>;

  const total = balance?.totalBalance || 0;
  const earnings = balance?.earningsCredits || 0;
  const credits = balance?.adminCredits || 0;
  const refunds = balance?.refundCredits || 0;

  return (
    <div className="max-w-[600px] mx-auto px-6 py-8">
      <Link to="/driver" className="text-brand-primary font-semibold text-[0.9rem] mb-6 block">← Dashboard</Link>
      <h1 className="font-serif text-[1.8rem] mb-8">My Wallet</h1>

      <div className="card text-center mb-6" style={{ background: 'linear-gradient(135deg, #2dd4a8, #6ee7b7)', color: '#0a1628' }}>
        <div className="text-[0.9rem] font-medium opacity-70 mb-1">Total Balance</div>
        <div className="text-[2.5rem] font-extrabold">${total.toFixed(2)}</div>
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 mt-4 text-[0.85rem]">
          <span>Trip earnings: ${earnings.toFixed(2)}</span>
          <span>Credits: ${credits.toFixed(2)}</span>
          <span>Refunds: ${refunds.toFixed(2)}</span>
        </div>
      </div>

      <button
        onClick={handleCashout}
        disabled={cashingOut || total <= 0}
        className="w-full p-3.5 rounded-xl border-0 text-white cursor-pointer font-bold text-[1rem] mb-8"
        style={{ background: total > 0 ? '#0a1628' : '#cbd5e1', cursor: total > 0 ? 'pointer' : 'not-allowed' }}
      >
        {cashingOut ? 'Requesting…' : 'Request cashout'}
      </button>

      <h3 className="font-bold mb-4">Recent Transactions</h3>
      {transactions.length === 0 ? (
        <p className="text-muted text-center p-8">No transactions yet.</p>
      ) : transactions.map((tx) => <TxRow key={tx.id} tx={tx} />)}
      <BackButtonBottom to="/driver" label="Dashboard" />
    </div>
  );
}

// Single transaction row. Trip-earning rows expand to show the fare split and
// offer a downloadable earnings statement / tax invoice PDF.
function TxRow({ tx }) {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const isEarning = tx.type === 'trip_earning';
  const positive = tx.type === 'credit' || tx.type === 'trip_earning' || (tx.amount || 0) >= 0;
  const dateStr = tx.createdAt?.toDate?.()?.toLocaleDateString('en-AU') || '—';
  const invoiceLines = tx.invoice?.lines || [];

  const handleDownload = async () => {
    if (!tx.bookingId) { toast.error('No trip linked to this transaction'); return; }
    setDownloading(true);
    try {
      const res = await getDriverTripInvoice(tx.bookingId);
      downloadFromSignedUrl(res.url, `reservo-earnings-${tx.bookingRef || tx.bookingId}.pdf`);
    } catch (err) {
      toast.error(err.message || 'Could not generate the statement');
    }
    setDownloading(false);
  };

  return (
    <div style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
      <div
        className={`flex justify-between py-3.5 ${isEarning ? 'cursor-pointer' : ''}`}
        onClick={isEarning ? () => setOpen((v) => !v) : undefined}
      >
        <div>
          <div className="font-semibold text-[0.92rem]">
            {tx.description || (positive ? 'Credit' : 'Debit')}
            {isEarning && <span className="text-muted text-[0.72rem] font-normal ml-2">{open ? '▲' : '▼'}</span>}
          </div>
          <div className="text-muted text-[0.82rem]">
            {dateStr}
            {isEarning && typeof tx.driverPercent === 'number' && <> · {tx.driverPercent}% share</>}
          </div>
        </div>
        <div className="font-bold" style={{ color: positive ? '#2dd4a8' : '#ff6b6b' }}>
          {positive ? '+' : '-'}${Math.abs(tx.amount || 0).toFixed(2)}
        </div>
      </div>

      {isEarning && open && (
        <div className="pb-4 pl-1">
          <div className="p-3 rounded-xl bg-slate-50">
            {invoiceLines.length > 0 ? invoiceLines.map((line, i) => (
              <div key={i} className="flex justify-between text-[0.82rem] py-0.5">
                <span className="text-muted">{line.label}</span>
                <span className="text-body">${(line.amount || 0).toFixed(2)}</span>
              </div>
            )) : (
              <div className="text-[0.82rem] text-muted">Breakdown not available for this trip.</div>
            )}
            <div className="flex justify-between text-[0.85rem] font-bold pt-2 mt-1 text-brand-dark" style={{ borderTop: '1px solid #e2e8f0' }}>
              <span>Paid to your wallet</span>
              <span>${(tx.invoice?.total ?? tx.amount ?? 0).toFixed(2)}</span>
            </div>
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="w-full mt-3 p-2.5 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-semibold text-[0.85rem]"
            >
              {downloading ? 'Preparing…' : '📄 Download earnings statement'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}